import type { ComponentDocMap } from './types'

/**
 * Documentation for cross-component UiConfigProps fields.
 * Includes both canonical prop keys and shared JSON-editor path aliases.
 * Keep in sync with UiConfigProps in src/api/uiConfig/index.d.ts
 */
export const commonPropDocs: ComponentDocMap = {
  props: {
    description:
      'Root object containing component-specific properties. Most hover docs for rendered behavior refer to fields inside this object.',
    type: 'UiConfigProps',
    example: '{ "title": "__My widget", "items": [...] }',
  },
  items: {
    description:
      'Common item array used by many components. Each item usually maps to data using data_id and provides display metadata such as title, unit, and color.',
    type: 'unknown[]',
    example: '[{ "title": "__Boiler A", "data_id": "boiler_a" }]',
  },
  'items[].title': {
    description: 'Display title for an item entry in items arrays.',
    type: 'string',
    example: '"__Boiler A"',
  },
  'items[].data_id': {
    description: 'Dataset binding key for an item entry in items arrays.',
    type: 'string',
    example: '"boiler_a"',
  },
  'items[].color': {
    description: 'Display color for an item (hex or alias such as $color-1).',
    type: 'string',
    example: '"$color-1"',
  },
  'items[].unit': {
    description: 'Unit string used when formatting the item value.',
    type: 'string',
    example: '"MW"',
  },
  'items[].decimals': {
    description: 'Decimal precision used when rendering values.',
    type: 'number',
    example: '1',
  },
  'items[].data_type': {
    description: 'Formatting type for item values (for example cost, energy, power).',
    type: 'string',
    example: '"energy"',
  },
  'items[].tooltip': {
    description: 'Optional tooltip text for the item.',
    type: 'string',
    example: '"__More information"',
  },
  'items[].stack_group': {
    description: 'Grouping key used to stack or group related item series/segments.',
    type: 'number | string',
    example: '0',
  },
  'items[].tag': {
    description: 'Single tag label used for filtering/grouping logic and base-item selection.',
    type: 'string',
    example: '"prod"',
  },
  'items[].tags': {
    description: 'List of digital-twin tags associated with the item for tag-based filtering.',
    type: 'string[]',
    example: '["company/bristad", "fuel/biomass"]',
  },
  has_background_color: {
    description: 'Applies a background color style to rendered rows/cards/items where supported.',
    type: 'boolean',
    example: 'true',
  },
  datasetStartTime: {
    description: 'Optional explicit dataset start time override.',
    type: 'ISODateTime',
    example: '"2026-03-01T00:00:00Z"',
  },
  datasetEndTime: {
    description: 'Optional explicit dataset end time override.',
    type: 'ISODateTime',
    example: '"2026-03-02T00:00:00Z"',
  },
  override_alias: {
    description: 'Alias overrides applied to child/nested UiConfig rendering.',
    type: 'UiConfigAliases',
    example: '{ "opt_job_type_id": 4 }',
  },
  override_props: {
    description: 'Props overrides applied to child/nested UiConfig rendering.',
    type: 'UiConfigOverrideProps',
    example: '{ "title": "__Override title" }',
  },
  base_items: {
    description:
      "Pulls items from the system's base_config into this component. Use { all: true } to include all base items, or list specific data_ids with true/false.",
    type: 'Record<string, boolean> | { all: true }',
    example: '{ "all": true }  or  { "heat_unit_a": true, "heat_unit_b": false }',
  },
  include_only_base_items_with_attr: {
    description:
      'Filters base_items to only those where the given attribute equals the given value. Applied before exclude rules.',
    type: 'Record<string, boolean | number | string | null>',
    example: '{ "h_energy_bar": true }',
  },
  base_items_filter: {
    description:
      'Applies a DSI filter override to all dataset instructions generated from base_items. Note: currently applied to all DSIs, not only base-item-derived ones.',
    type: 'DatasetInstructionFilter',
    example: '{ "aggregate": "sum", "aggregate_in_frontend": true }',
  },
  multi_base_items_override: {
    description:
      'Creates multiple clones of the base item set, each with different alias overrides and return_id prefixes. Used to render "optimal" and "outcome" side by side from the same base items.',
    type: 'UiConfigBaseItemOverride[]',
    example:
      '[{ return_id_prefix: "optimal.", alias: { opt_job_type_id: 3 }, item: { stack_group: 0 } }, { return_id_prefix: "outcome.", alias: { opt_job_type_id: 5 }, item: { stack_group: 1 } }]',
  },
  'multi_base_items_override[].return_id_prefix': {
    description: 'Prepended to every return_id and data_id generated for this override group.',
    type: 'string',
    example: '"optimal."',
  },
  'multi_base_items_override[].alias': {
    description: 'Alias overrides applied when resolving dataset instructions for this group.',
    type: 'UiConfigAliases',
    example: '{ "opt_job_type_id": 4, "optjob_type": "followup_without_deviations" }',
  },
  'multi_base_items_override[].calc_override': {
    description:
      'Instead of cloning base DSIs, generates a calc DSI per item that computes the result from other prefixed groups.',
    type: '{ operator: string; source_prefixes: [string, string]; null_as_zero?: boolean }',
    example: '{ "operator": "-", "source_prefixes": ["optimal.", "outcome."] }',
  },
  multi_base_item_alias_renaming: {
    description:
      'Simpler alternative to multi_base_items_override. Renders the same base items multiple times using different alias sets. Items are not prefixed.',
    type: 'UiConfigAliases[]',
    example: '[{ "opt_job_type_id": 2 }, { "opt_job_type_id": 5 }]',
  },
  base_items_conditional_overrides: {
    description:
      'Conditionally overrides properties on base items by matching item attributes. Works with any config that uses base_items. Runs after item filtering and modifies items in place without creating duplicates. Later entries in the array win on conflict when multiple entries match the same item.',
    type: 'Array<{ match_attr: Record<string, boolean | number | string | null>; item: Partial<UiConfigPropsItem> }>',
    example: '[{ "match_attr": { "is_el": true }, "item": { "stack_group": 1 } }]',
  },
  'base_items_conditional_overrides[].match_attr': {
    description:
      'Attribute-value pairs that must all match on a base item for the override to apply (AND logic). Compared by strict equality against item properties.',
    type: 'Record<string, boolean | number | string | null>',
    example: '{ "is_el": true }',
  },
  'base_items_conditional_overrides[].item': {
    description:
      'Partial item properties spread onto every base item that matches match_attr. Applied early in the merge pipeline, so downstream processing (e.g. multi_base_items_override item spreads) can still override these values.',
    type: 'Partial<UiConfigPropsItem>',
    example: '{ "stack_group": 1, "color": "#ff0000" }',
  },
  include_items_in_filtering: {
    description: "Enables the digital twin tag-based filter component for this config's items.",
    type: 'boolean',
    example: 'true',
  },
  tags_config: {
    description:
      'Maps tag strings to display overrides (color, label). Merged with the base_config tags_config at runtime.',
    type: 'Record<string, { color: string }>',
    example: '{ "company/bristad": { "color": "#22adbe" } }',
  },
}

/**
 * Documentation for root-level UiConfig fields (outside props).
 * Keep in sync with UiConfig in src/api/uiConfig/index.d.ts
 */
export const rootFieldDocs: ComponentDocMap = {
  component: {
    description: 'Identifies which registered React component renders this config.',
    type: 'string',
    example: '"chart"',
  },
  display_name: {
    description: 'Human-readable label shown in the /uiconfig editor list.',
    type: 'string',
    example: '"[chart] Follow-up production - optimal"',
  },
  children_ids: {
    description:
      'IDs (not UIDs) of child UiConfigs rendered inside this component. Children are rendered in array order. The runtime resolves each ID to the active published version.',
    type: 'number[]',
    example: '[42, 43]',
  },
  alias: {
    description:
      'Key-value pairs substituted into $key references anywhere in this config at parse time. Later entries in the resolution order win.',
    type: 'UiConfigAliases',
    example: '{ "opt_job_type_id": 4, "optjob_type": "followup_without_deviations" }',
  },
  dataset_instructions: {
    description:
      'Ordered list of data fetch/compute instructions. Results are referenced by return_id from props.items[].data_id.',
    type: 'DatasetInstruction[]',
    example:
      '[{ "type": "template", "return_id": "rated_power", "contract": {}, "filter": { "start_time": "$start_time", "end_time": "$end_time" } }]',
  },
  mode: {
    description:
      '"dev" = visible only in developer mode. "beta" = visible only for beta users. "deleted" = soft-deleted, not rendered.',
    type: '"dev" | "beta" | "deleted"',
    example: '"dev"',
  },
  is_published: {
    description: 'Only published configs are rendered in the app. Drafts are visible only in the /uiconfig editor.',
    type: 'boolean',
    example: 'true',
  },
}
